import { toast } from "@carbon6/c6-frontend-design";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { changeDiagnosticJob, type DiagnosticJobAction } from "@/api/adminDiagnosticJob";
import { adminOperationError } from "@/app/adminOperationError";
import { openDatasetDetailWindow } from "@/app/datasetDetailWindow";
import { windowStack } from "@/app/windowStack";
import { useWorkerJobDetail, type WorkerJobDetail } from "./useWorkerJobDetail";

export type WorkerJobTarget = { kind: WorkerJobDetail["kind"]; uuid: string };

export type WorkerJobDetailModalProps = {
  kind: WorkerJobDetail["kind"];
  uuid: string;
  onClose: () => void;
  onChanged: () => Promise<void>;
};
export type JobDetailTab = "overview" | "details";

export function useWorkerJobDetailController({
  kind,
  uuid,
  onClose,
  onChanged,
}: WorkerJobDetailModalProps) {
  const { activeId } = useSyncExternalStore(windowStack.subscribe, windowStack.getSnapshot);
  // Dataset 창에서 돌아오면 같은 작업을 다시 조회한다.
  const active = activeId === null;
  const { detail, error, refresh } = useWorkerJobDetail(kind, uuid, active);
  const [tab, setTab] = useState<JobDetailTab>("overview");
  const [pending, setPending] = useState<DiagnosticJobAction | null>(null);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const save = async () => {
    if (pending === null || busy) return;
    setBusy(true);
    try {
      await changeDiagnosticJob(uuid, pending);
      if (alive.current) toast.success("요청을 접수했습니다. 최신 상태를 확인합니다.");
    } catch (caught) {
      if (alive.current) toast.error(adminOperationError(caught));
    } finally {
      if (alive.current) {
        setBusy(false);
        setPending(null);
        refresh();
        void onChanged();
      }
    }
  };
  const openDataset = () => {
    if (detail === null || detail.job.dataset_uuid === null) return;
    openDatasetDetailWindow({
      datasetUuid: detail.job.dataset_uuid,
      displayName:
        detail.kind === "upload" ? detail.job.display_name : detail.job.dataset_display_name,
      initialTab: "review",
    });
  };
  return {
    active,
    detail,
    error,
    refresh,
    tab,
    setTab,
    pending,
    setPending,
    busy,
    save,
    openDataset,
    close: () => {
      if (!busy) onClose();
    },
  };
}
